import React, { useState, useEffect, useCallback } from "react";
import { Box, Grid, Typography, Pagination, Stack } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import PaymentMethodCard from "../components/paymentMethods/PaymentMethodCard";
import CreatePaymentMethodModal from "../components/paymentMethods/CreatePaymentMethodModal";
import UpdatePaymentMethodModal from "../components/paymentMethods/UpdatePaymentMethodModal";
import AppCircularProgress from "../components/common/AppCircularProgress";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getPaymentMethods,
  deletePaymentMethod,
  resetPaymentMethodServerStatus,
} from "../redux/actions/paymentMethodActions";
import PageError from "../components/common/PageError";
import { PaymentMethodResponseDto } from "../shared/dtos/paymentMethod/PaymentMethodResponseDto";

const PaymentMethodsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: paymentMethods,
    pagination,
    loadingFetchAll: loadingPaymentMethods,
    errorFetchAll: errorLoadingPaymentMethods,
    serverStatus,
    loadingDelete: loadingPaymentMethodDelete,
    loadingUpdate: loadingPaymentMethodUpdate,
    loadingCreate: loadingPaymentMethodCreate,
    errorCreate: errorCreatingPaymentMethod,
    errorUpdate: errorUpdatingPaymentMethod,
  } = useAppSelector((state) => state.paymentMethod);

  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(12);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedMethod, setSelectedMethod] =
    useState<PaymentMethodResponseDto | null>(null);

  const canCreate = hasPermission("payment_method:create");
  const canUpdate = hasPermission("payment_method:update");
  const canDelete = hasPermission("payment_method:delete");

  const fetchMethods = useCallback(() => {
    dispatch(getPaymentMethods({ page, size: limit, search: debouncedSearch }));
  }, [dispatch, page, limit, debouncedSearch]);

  useEffect(() => {
    fetchMethods();
  }, [fetchMethods]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback(
    () => !loadingPaymentMethodCreate && setIsCreateModalOpen(false),
    [loadingPaymentMethodCreate],
  );

  const handleOpenUpdate = (method: PaymentMethodResponseDto): void => {
    setSelectedMethod(method);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback(() => {
    if (!loadingPaymentMethodUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedMethod(null);
    }
  }, [loadingPaymentMethodUpdate]);

  const handleOpenDelete = (method: PaymentMethodResponseDto): void => {
    setSelectedMethod(method);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback(() => {
    if (!loadingPaymentMethodDelete) {
      setIsDeleteModalOpen(false);
      setSelectedMethod(null);
    }
  }, [loadingPaymentMethodDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedMethod) dispatch(deletePaymentMethod(selectedMethod.id));
  };

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;
    if (operation === "create" && (status === 201 || status === 200))
      handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();
    dispatch(resetPaymentMethodServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  const renderContent = (): React.JSX.Element => {
    if (loadingPaymentMethods) return <AppCircularProgress />;
    if (errorLoadingPaymentMethods)
      return (
        <PageError error={errorLoadingPaymentMethods} onRetry={fetchMethods} />
      );

    if (!paymentMethods || paymentMethods.length === 0)
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography>No payment methods found.</Typography>
        </Box>
      );

    return (
      <>
        <Grid container spacing={3}>
          {paymentMethods.map((method) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={method.id}>
              <PaymentMethodCard
                method={method}
                onEdit={canUpdate ? () => handleOpenUpdate(method) : undefined}
                onDelete={
                  canDelete ? () => handleOpenDelete(method) : undefined
                }
              />
            </Grid>
          ))}
        </Grid>
        {pagination && pagination.totalPages! > 1 && (
          <Stack sx={{ alignItems: "center", mt: 4 }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(_e, v) => setPage(v)}
              color="primary"
            />
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer
      title="Payment Methods"
      subtitle="Manage how customers pay you and link methods to financial accounts."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Add Method
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search methods..."
      />
      {renderContent()}

      {isCreateModalOpen && (
        <CreatePaymentMethodModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingPaymentMethodCreate}
          error={errorCreatingPaymentMethod}
        />
      )}
      {isUpdateModalOpen && selectedMethod && (
        <UpdatePaymentMethodModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          method={selectedMethod}
          loading={loadingPaymentMethodUpdate}
          error={errorUpdatingPaymentMethod}
        />
      )}
      {isDeleteModalOpen && selectedMethod && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Method"
          description={`Are you sure you want to delete "${selectedMethod.name}"?`}
          loading={loadingPaymentMethodDelete}
          severity="error"
        />
      )}
    </PageContainer>
  );
};

export default PaymentMethodsPage;
